/**
 * One change request to a committed hiring-plan role, with its three verdicts.
 *
 * Deliberately built from the proposal card's own parts — `ApprovalControls`, `DecisionPill`, the
 * same `Fact` rows — because the decision is the same decision taken by the same three people under
 * the same rules (David, Sep 25 2026). What differs is what approval DOES: nothing reaches the
 * Hiring Database until the third approval, and the card says so rather than letting "approved"
 * read as "already in the plan".
 */
import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import type {
  ApprovalDecision,
  ApproverKey,
  ApprovalViewer,
  ChangeRequestState,
  RoleChangeRequest,
} from '../../types/roleApprovals';
import { formatEuro, type RoleCompensation } from '../../types/compensation';
import { ApprovalControls } from './ApprovalControls';
import { DecisionPill, Fact, RADIUS, shortDate } from './approvalUi';

const STATE_STYLES: Record<ChangeRequestState, string> = {
  pending: 'border-amber-200 bg-amber-50 text-amber-800',
  approved: 'border-amber-200 bg-amber-50 text-amber-800',
  declined: 'border-red-200 bg-red-50 text-red-700',
  applied: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  conflict: 'border-red-200 bg-red-50 text-red-700',
  withdrawn: 'border-zinc-200 bg-zinc-50 text-zinc-500',
  superseded: 'border-zinc-200 bg-zinc-50 text-zinc-500',
};

const STATE_LABELS: Record<ChangeRequestState, string> = {
  pending: 'Awaiting approval',
  approved: 'Approved — not yet applied',
  declined: 'Declined',
  applied: 'Applied',
  conflict: 'Not applied',
  withdrawn: 'Withdrawn',
  superseded: 'Replaced by a newer request',
};

const yearly = (c: RoleCompensation | null | undefined) =>
  c && !c.unavailable ? (c.ote ?? c.average ?? null) : null;

function costLine(label: string, before: number | null | undefined, after: number | null | undefined) {
  const show = (v: number | null | undefined) => (Number.isFinite(v) ? formatEuro(v as number) : 'not costed');
  return (
    <div className="flex gap-1.5 text-xs">
      <span className="text-zinc-400">{label}</span>
      <span className="text-zinc-700">
        {show(before)} → <strong>{show(after)}</strong>
      </span>
    </div>
  );
}

interface Props {
  request: RoleChangeRequest;
  viewer: ApprovalViewer;
  busy: boolean;
  onDecide: (requestId: string, approverKey: ApproverKey, decision: ApprovalDecision, reason?: string) => void;
  onApply: (requestId: string) => void;
  onWithdraw: (requestId: string) => void;
}

export function ChangeRequestCard({ request, viewer, busy, onDecide, onApply, onWithdraw }: Props) {
  const [showLog, setShowLog] = useState(false);
  const decidable = request.state === 'pending' || request.state === 'declined';
  const mine = viewer.email.toLowerCase() === request.requestedBy;
  const canWithdraw =
    (request.state === 'pending' || request.state === 'declined') && (mine || viewer.isAdmin);
  const canApply = request.state === 'approved' && (Boolean(viewer.approverKey) || viewer.isAdmin);
  const cost = request.cost;

  return (
    <article className={`${RADIUS} border border-zinc-200 bg-white p-4`}>
      <header className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-wide text-zinc-400">Change to a planned role</p>
          <h2 className="text-sm font-semibold text-zinc-900">{request.title}</h2>
        </div>
        <span
          className={`inline-flex items-center ${RADIUS} border px-2 py-0.5 text-xs font-medium ${STATE_STYLES[request.state]}`}
        >
          {STATE_LABELS[request.state]}
        </span>
      </header>

      {request.descriptions.length > 0 && (
        <ul className="mb-3 list-disc pl-4 text-xs text-zinc-700">
          {request.descriptions.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}

      <div className="mb-3 grid grid-cols-1 gap-y-1 sm:grid-cols-2">
        {costLine('Salary basis / year', yearly(cost?.before), yearly(cost?.after))}
        {costLine(
          `Until EOY ${cost?.after?.year ?? ''}`.trim(),
          cost?.before?.costUntilYearEnd,
          cost?.after?.costUntilYearEnd
        )}
        <Fact label="Requested by" value={request.requestedBy} />
        <Fact label="Requested on" value={shortDate(request.createdAt)} />
      </div>

      {request.reason === 'uncostable' && (
        <p className="mb-3 text-xs text-amber-800">
          The role cannot be costed against its salary band before and after this change, so it could not be checked
          against the 10% limit.
        </p>
      )}

      <div className="flex flex-col gap-3 border-t border-zinc-100 pt-3">
        {request.approvals.map((approver) => {
          const isSelf = viewer.approverKey === approver.key;
          // A former holder's decision is theirs and stays as it is: no controls on it.
          const canDecide = decidable && !approver.decidedByName && (isSelf || viewer.isAdmin);
          return (
            <div key={approver.key} className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex min-w-0 flex-wrap items-baseline gap-2">
                <span className="text-xs font-medium text-zinc-700">{approver.title}</span>
                <span className="text-xs text-zinc-500">{approver.decidedByName ?? approver.name}</span>
                <DecisionPill decision={approver.decision} />
                {approver.decidedAt && <span className="text-xs text-zinc-500">{shortDate(approver.decidedAt)}</span>}
              </div>
              {canDecide && (
                <ApprovalControls
                  approver={approver}
                  isSelf={isSelf}
                  isDelegate={isSelf && viewer.isDelegate}
                  disabled={busy}
                  onDecide={(key, decision, reason) => onDecide(request.id, key, decision, reason)}
                />
              )}
            </div>
          );
        })}
      </div>

      {request.state === 'pending' && (
        <p className="mt-3 text-xs text-zinc-500">
          Nothing changes in the Hiring Database until all three have approved. The role cannot be edited meanwhile.
        </p>
      )}
      {request.state === 'applied' && (
        <p className="mt-3 text-xs text-emerald-700">Written to the Hiring Database on {shortDate(request.appliedAt)}.</p>
      )}
      {(request.state === 'approved' || request.state === 'conflict') && request.applyError && (
        <div className={`${RADIUS} mt-3 border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800`}>
          <p>{request.applyError}</p>
          {canApply && (
            <Button size="sm" variant="secondary" className="mt-2" disabled={busy} onClick={() => onApply(request.id)}>
              Apply it now
            </Button>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-3">
        {canWithdraw && (
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => onWithdraw(request.id)}>
            Withdraw request
          </Button>
        )}
        {request.log.length > 0 && (
          <button
            type="button"
            aria-expanded={showLog}
            className="text-xs text-zinc-500 underline underline-offset-2 hover:text-zinc-700"
            onClick={() => setShowLog((open) => !open)}
          >
            {showLog ? 'Hide' : 'Show'} decision history
          </button>
        )}
      </div>
      {showLog && (
        <pre className="mt-2 whitespace-pre-wrap break-words text-xs text-zinc-600">{request.log.join('\n')}</pre>
      )}
    </article>
  );
}
